import { index, integer, sqliteTable, text } from 'drizzle-orm/sqlite-core';
import { syncedColumns } from './common';

/** `cash`, `account`, or `card:<payee_id>` (spec §7). */
export type LedgerMethod = 'cash' | 'account' | `card:${string}`;

/**
 * Synced. Every money movement (spec §7 LedgerEntry).
 * Counting: expense and refund count in Spending breakdowns; transfer never does;
 * income rows come only from received paydays and bonuses.
 */
export const ledgerEntries = sqliteTable(
  'ledger_entries',
  {
    ...syncedColumns(),
    /** Local calendar day 'YYYY-MM-DD' in recordedTz. */
    date: text('date').notNull(),
    /** Always positive; kind gives the direction. Centavos. */
    amountMinor: integer('amount_minor').notNull(),
    currency: text('currency', { enum: ['PHP'] })
      .notNull()
      .default('PHP'),
    kind: text('kind', { enum: ['expense', 'income', 'transfer', 'refund'] }).notNull(),
    /** Null = uncategorized (a normal state). */
    categoryId: text('category_id'),
    method: text('method').$type<LedgerMethod>(),
    /** Set when the entry deducts from or refills a fund. */
    fundId: text('fund_id'),
    /**
     * v0.5 Accounts: the money account the entry moved money out of (expense, transfer) or into
     * (income, refund). Null = no account (old entries, card purchases): it changes no balance.
     */
    accountId: text('account_id'),
    /** v0.5: where a transfer's money went (null for a card statement payment or a withdrawal). */
    toAccountId: text('to_account_id'),
    /** A ModuleId, e.g. 'spending', 'quit_smoking', 'payments'. */
    sourceModule: text('source_module').notNull(),
    /** Row in the source module, e.g. a smoke log or payment due id. */
    sourceId: text('source_id'),
    note: text('note'),
    /** IANA time zone the date was recorded in, e.g. 'Asia/Manila'. */
    recordedTz: text('recorded_tz').notNull(),
  },
  (t) => [
    index('ledger_entries_user_date_idx').on(t.userId, t.date),
    index('ledger_entries_user_source_idx').on(t.userId, t.sourceModule, t.sourceId),
    index('ledger_entries_user_category_idx').on(t.userId, t.categoryId),
    index('ledger_entries_user_fund_idx').on(t.userId, t.fundId),
    index('ledger_entries_user_account_idx').on(t.userId, t.accountId),
  ],
);
